import { HoverCard, HoverCardContent, HoverCardTrigger } from '@crowdex/ui/components/hover-card';
import {
  FileCode2,
  GitPullRequest,
  Globe2,
  ListChecks,
  MessageSquare,
  MousePointerClick,
  Terminal,
  X,
} from 'lucide-react';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import { elementSelectionDetail } from '@/lib/element-selection';

/**
 * Row of context chips shown above the composer: element selections (web pages', apps') and chat / file
 * / terminal annotations queued for the next prompt. Reads and removes them
 * directly through the desktop shell store.
 */
export function ComposerContextChips() {
  const elementSelections = useDesktopShellStore((state) => state.elementSelections);
  const responseAnnotations = useDesktopShellStore((state) => state.responseAnnotations);
  const setElementSelections = useDesktopShellStore((state) => state.setElementSelections);
  const setResponseAnnotations = useDesktopShellStore((state) => state.setResponseAnnotations);

  if (elementSelections.length === 0 && responseAnnotations.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 px-3 pt-3">
      {elementSelections.map((selection, index) => {
        const detail = elementSelectionDetail(selection.context);
        const SourceIcon = selection.kind === 'web' ? Globe2 : MousePointerClick;
        return (
          <HoverCard key={selection.id} openDelay={250}>
            <HoverCardTrigger asChild>
              <div className="group flex h-12 max-w-72 items-center gap-2.5 rounded-xl border border-hairline-strong bg-card p-1.5 pr-2 shadow-sm transition-colors hover:border-foreground/20">
                {selection.dataUrl === undefined ? (
                  <span className="flex h-9 w-11 shrink-0 items-center justify-center rounded-lg border border-hairline bg-background text-muted-foreground">
                    <SourceIcon className="size-4" />
                  </span>
                ) : (
                  <span className="relative h-9 w-11 shrink-0 overflow-hidden rounded-lg border border-hairline bg-background">
                    <img alt="" className="size-full object-cover" src={selection.dataUrl} />
                    <span className="absolute right-0.5 bottom-0.5 flex size-3.5 items-center justify-center rounded-full bg-background/90 shadow-sm backdrop-blur-sm">
                      <SourceIcon className="size-2.5 text-muted-foreground" />
                    </span>
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate type-caption font-medium text-foreground">
                    Annotation {index + 1}
                  </span>
                  <span className="block truncate type-caption text-muted-foreground">
                    {selection.kind === 'web' ? 'Sélection web' : detail.source}
                  </span>
                </span>
                <button
                  aria-label={`Supprimer l’annotation ${index + 1}`}
                  className="flex size-control-xs shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-60 transition-[background-color,color,opacity] hover:bg-background hover:text-foreground group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() =>
                    setElementSelections((current) =>
                      current.filter((candidate) => candidate.id !== selection.id),
                    )
                  }
                  type="button"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            </HoverCardTrigger>
            <HoverCardContent
              align="start"
              className="w-72 overflow-hidden rounded-xl p-0"
              sideOffset={8}
            >
              {selection.dataUrl !== undefined && (
                <img
                  alt="Aperçu de l’annotation"
                  className="h-32 w-full border-hairline border-b bg-muted object-cover"
                  src={selection.dataUrl}
                />
              )}
              <div className="space-y-2.5 p-3">
                <div className="type-caption flex items-center gap-2 text-muted-foreground">
                  <SourceIcon className="size-3.5 shrink-0" />
                  <span className="truncate">{detail.source}</span>
                </div>
                <p className="line-clamp-2 type-ui font-medium">{detail.target}</p>
                {detail.annotation !== null && (
                  <p className="line-clamp-3 rounded-lg bg-muted/70 px-2.5 py-2 type-caption text-muted-foreground">
                    {detail.annotation}
                  </p>
                )}
              </div>
            </HoverCardContent>
          </HoverCard>
        );
      })}
      {responseAnnotations.map((annotation, annotationIndex) => {
        const index = elementSelections.length + annotationIndex;
        const AnnotationIcon =
          annotation.kind === 'terminal'
            ? Terminal
            : annotation.kind === 'file'
              ? FileCode2
              : annotation.kind === 'plan'
                ? ListChecks
                : annotation.kind === 'pull_request'
                  ? GitPullRequest
                  : MessageSquare;
        return (
          <HoverCard key={annotation.id} openDelay={250}>
            <HoverCardTrigger asChild>
              <div className="group flex h-12 max-w-72 items-center gap-2.5 rounded-xl border border-hairline-strong bg-card p-1.5 pr-2 shadow-sm transition-colors hover:border-foreground/20">
                <span className="flex h-9 w-11 shrink-0 items-center justify-center rounded-lg border border-hairline bg-background text-muted-foreground">
                  <AnnotationIcon className="size-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate type-caption font-medium text-foreground">
                    Annotation {index + 1}
                  </span>
                  <span className="block truncate type-caption text-muted-foreground">
                    {annotation.kind === 'terminal'
                      ? 'Sélection terminal'
                      : annotation.kind === 'file'
                        ? 'Sélection fichier'
                        : annotation.kind === 'plan'
                          ? 'Annotation du plan'
                          : annotation.kind === 'pull_request'
                            ? 'Problème de pull request'
                            : 'Sélection chat'}
                  </span>
                </span>
                <button
                  aria-label={`Supprimer l’annotation ${index + 1}`}
                  className="flex size-control-xs shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-60 transition-[background-color,color,opacity] hover:bg-background hover:text-foreground group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() =>
                    setResponseAnnotations((current) =>
                      current.filter((candidate) => candidate.id !== annotation.id),
                    )
                  }
                  type="button"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            </HoverCardTrigger>
            <HoverCardContent align="start" className="w-72 rounded-xl p-3" sideOffset={8}>
              <div className="space-y-2.5">
                <div className="type-caption flex items-center gap-2 text-muted-foreground">
                  <AnnotationIcon className="size-3.5 shrink-0" />
                  <span className="truncate">{annotation.source}</span>
                </div>
                <p className="line-clamp-3 whitespace-pre-wrap type-ui font-medium">
                  {annotation.text}
                </p>
                {annotation.annotation.length > 0 && (
                  <p className="line-clamp-3 rounded-lg bg-muted/70 px-2.5 py-2 type-caption text-muted-foreground">
                    {annotation.annotation}
                  </p>
                )}
              </div>
            </HoverCardContent>
          </HoverCard>
        );
      })}
    </div>
  );
}
